import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Modal, Form, Spin, Select, Row, Col, Tabs } from 'antd';
import { FaRegTrashAlt, FaEdit, FaUndo } from 'react-icons/fa';
import { PlusOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const { Option } = Select;
const { TabPane } = Tabs;

const Usuario = () => {
  const [usuariosAtivos, setUsuariosAtivos] = useState([]);
  const [usuariosInativos, setUsuariosInativos] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  useEffect(() => {
    document.title = 'Usuários';
    fetchUsuariosAtivos();
    fetchUsuariosInativos();
    fetchPerfis();
  }, []);

  const sms_error = (msg) => toast.error(msg);
  const sms_success = (msg) => toast.success(msg);

  const fetchUsuariosAtivos = async () => {
    setLoading(true);
    try {
      const response = await Api.ListUser();
      const dados = Array.isArray(response) ? response : [];
      setUsuariosAtivos(dados.map(item => ({ key: item.id, ...item })));
    } catch {
      sms_error('Erro ao buscar usuários ativos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsuariosInativos = async () => {
    try {
      const response = await Api.ListUserInativo();
      const dados = Array.isArray(response) ? response : [];
      setUsuariosInativos(dados.map(item => ({ key: item.id, ...item })));
    } catch {
      sms_error('Erro ao buscar usuários inativos');
    }
  };

  const fetchPerfis = async () => {
    try {
      const response = await Api.ListPerfil();
      setPerfis(Array.isArray(response) ? response : []);
    } catch {
      sms_error('Erro ao buscar perfis');
    }
  };

  const passwordRules = [
    { required: true, message: 'A senha é obrigatória' },
    {
      pattern: /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{6,}$/,
      message: 'A senha deve ter pelo menos 6 caracteres, uma letra maiúscula e um caractere especial'
    }
  ];

  const handleAdd = async (values) => {
    setAddLoading(true);
    try {
      const result = await Api.AddUsuario(values);
      if (result.success) {
        sms_success('Usuário adicionado com sucesso!');
        setIsAddModalVisible(false);
        form.resetFields();
        await fetchUsuariosAtivos();
      } else {
        sms_error(result.message || 'Erro ao adicionar usuário');
      }
    } catch (error) {
      sms_error('Erro ao adicionar usuário: ' + error.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleEdit = async (values) => {
    setEditLoading(true);
    try {
      const result = await Api.AlterUser({ id: selectedUser.key, ...values });
      if (result.success) {
        sms_success('Usuário atualizado com sucesso!');
        setIsEditModalVisible(false);
        editForm.resetFields();
        await fetchUsuariosAtivos();
      } else {
        sms_error(result.message || 'Erro ao editar usuário');
      }
    } catch (error) {
      sms_error('Erro ao editar usuário: ' + error.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const result = await Api.DeleteUser({ id: userToDelete });
      if (result.success) {
        sms_success('Usuário excluído com sucesso!');
        await fetchUsuariosAtivos();
      } else {
        sms_error(result.message || 'Erro ao excluir usuário');
      }
    } catch {
      sms_error('Erro ao excluir usuário');
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  const handleRestore = async (id) => {
    setRestoreLoading(true);
    try {
      const result = await Api.AlterUser({ id, status: 1 });
      if (result.success) {
        sms_success('Usuário restaurado com sucesso!');
        await fetchUsuariosAtivos();
        await fetchUsuariosInativos();
      } else {
        sms_error(result.message || 'Erro ao restaurar usuário');
      }
    } catch {
      sms_error('Erro ao restaurar usuário');
    } finally {
      setRestoreLoading(false);
    }
  };

  const columnsAtivos = [
    { title: 'Nome', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Perfil', dataIndex: 'perfil', key: 'perfil' },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 10 }}>
          <FaEdit
            style={{ cursor: 'pointer', color: '#1890ff' }}
            onClick={() => {
              setSelectedUser(record);
              editForm.setFieldsValue({
                nome: record.name,
                email: record.email,
                id_perfil: perfis.find(p => p.descricao === record.perfil)?.id
              });
              setIsEditModalVisible(true);
            }}
          />
          <FaRegTrashAlt
            style={{ cursor: 'pointer', color: '#ff4d4f' }}
            onClick={() => {
              setUserToDelete(record.key);
              setIsDeleteModalVisible(true);
            }}
          />
        </div>
      )
    }
  ];

  const columnsInativos = [
    { title: 'Nome', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Perfil', dataIndex: 'perfil', key: 'perfil' },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, record) => (
        <Button
          type="link"
          icon={<FaUndo style={{ color: 'green' }} />}
          onClick={() => handleRestore(record.key)}
          loading={restoreLoading}
        >
          Restaurar
        </Button>
      )
    }
  ];

  return (
    <div>
      <ToastContainer />

      <Tabs defaultActiveKey="1">
        <TabPane tab="Usuários Ativos" key="1">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsAddModalVisible(true)}
            >
              Adicionar
            </Button>
          </div>

          <Spin spinning={loading}>
            <Table columns={columnsAtivos} dataSource={usuariosAtivos} rowKey="key" />
          </Spin>
        </TabPane>

        <TabPane tab="Usuários Inativos" key="2">
          <Spin spinning={loading}>
            <Table columns={columnsInativos} dataSource={usuariosInativos} rowKey="key" />
          </Spin>
        </TabPane>
      </Tabs>

      {/* Modal Adicionar */}
      <Modal
        title="Adicionar Usuário"
        open={isAddModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsAddModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item name="nome" label="Nome" rules={[{ required: true, message: 'Nome é obrigatório' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email é obrigatório' }]}>
            <Input type="email" />
          </Form.Item>
          <Form.Item name="password" label="Senha" rules={passwordRules}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="id_perfil" label="Perfil" rules={[{ required: true, message: 'Perfil é obrigatório' }]}>
            <Select placeholder="Selecione um perfil">
              {perfis.map((p) => (
                <Option key={p.id} value={p.id}>{p.descricao}</Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => {
                form.resetFields();
                setIsAddModalVisible(false);
              }}
              disabled={addLoading}
            >
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={addLoading}>
              {addLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Modal Editar */}
      <Modal
        title="Editar Usuário"
        open={isEditModalVisible}
        onCancel={() => {
          editForm.resetFields();
          setIsEditModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="nome" label="Nome" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true }]}>
            <Input type="email" />
          </Form.Item>
          <Form.Item name="password" label="Senha" rules={passwordRules}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="id_perfil" label="Perfil" rules={[{ required: true }]}>
            <Select placeholder="Selecione um perfil">
              {perfis.map((p) => (
                <Option key={p.id} value={p.id}>{p.descricao}</Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => {
                editForm.resetFields();
                setIsEditModalVisible(false);
              }}
              disabled={editLoading}
            >
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={editLoading}>
              {editLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Modal Excluir */}
      <Modal
        title="Confirmar Exclusão"
        open={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText="Sim"
        cancelText="Não"
        confirmLoading={deleteLoading}
      >
        <p>Tem certeza que deseja excluir este usuário?</p>
      </Modal>
    </div>
  );
};

export default Usuario;
